/**
 * The left column: everything the model is given before it writes.
 *
 * Raw notes in, media attached, platforms chosen, voice picked — then one CTA
 * that turns it into a draft per platform.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, Sparkles, Wand2 } from 'lucide-react';
import type { PlatformConnectionStatus } from '../../../shared/api';
import type {
  AiProvider,
  MediaAsset,
  PlatformId,
  SocialAccount,
  StyleProfile,
} from '../../../shared/types';
import { wordCount } from '../../../shared/utils';
import { Badge, Button, Card, Field, Input, Select, Switch, Textarea, Tooltip } from '../ui';
import { cn, isModifierEnter } from '../../lib/utils';
import { MediaStrip } from './MediaStrip';
import { PlatformSelector } from './PlatformSelector';
import type { SaveState } from './types';

export interface SourcePaneProps {
  title: string;
  onTitleChange: (value: string) => void;
  rawContent: string;
  onRawContentChange: (value: string) => void;

  assets: MediaAsset[];
  onAddMedia: () => void;
  onRemoveMedia: (assetId: string) => void;
  onMoveMedia: (assetId: string, direction: -1 | 1) => void;
  onGenerateImage: () => void;
  generatingImage: boolean;

  accounts: SocialAccount[];
  platformStatuses: PlatformConnectionStatus[];
  selectedPlatforms: PlatformId[];
  onTogglePlatform: (platform: PlatformId) => void;
  /** Rendered above the platform chips — e.g. "connect an account first". */
  connectionNotice?: ReactNode;

  styleProfiles: StyleProfile[];
  styleProfileId: string;
  onStyleProfileChange: (value: string) => void;

  instructions: string;
  onInstructionsChange: (value: string) => void;

  overwrite: boolean;
  onOverwriteChange: (value: boolean) => void;
  hasExistingDrafts: boolean;

  onGenerate: () => void;
  generating: boolean;
  saveState: SaveState;
  aiProvider: AiProvider;
  className?: string;
}

const PLACEHOLDER = `Paste anything: bullet points, a link, a rough paragraph, a quote from a customer.

• new espresso blend lands Friday
• single origin, Ethiopian, notes of blueberry
• 10% off the first week for members
• link: https://example.com/blends/aurora`;

export function SourcePane({
  title,
  onTitleChange,
  rawContent,
  onRawContentChange,
  assets,
  onAddMedia,
  onRemoveMedia,
  onMoveMedia,
  onGenerateImage,
  generatingImage,
  accounts,
  platformStatuses,
  selectedPlatforms,
  onTogglePlatform,
  connectionNotice,
  styleProfiles,
  styleProfileId,
  onStyleProfileChange,
  instructions,
  onInstructionsChange,
  overwrite,
  onOverwriteChange,
  hasExistingDrafts,
  onGenerate,
  generating,
  saveState,
  aiProvider,
  className,
}: SourcePaneProps) {
  const [showAdvanced, setShowAdvanced] = useState(Boolean(instructions));
  const words = wordCount(rawContent);
  const ready = rawContent.trim().length > 0 && selectedPlatforms.length > 0;

  return (
    <Card className={cn('overflow-visible', className)}>
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
        <div>
          <h2 className="text-sm font-semibold text-fg">Your raw material</h2>
          <p className="text-xs text-subtle">Notes in, a tailored draft per platform out.</p>
        </div>
        {saveState === 'saving' ? (
          <span className="text-[11px] text-subtle">Saving…</span>
        ) : saveState === 'saved' ? (
          <span className="text-[11px] text-success">Saved</span>
        ) : saveState === 'dirty' ? (
          <span className="text-[11px] text-subtle">Unsaved</span>
        ) : null}
      </div>

      <div className="space-y-5 px-5 py-4">
        <Field label="Working title" hint="Internal only — WordPress gets its own headline.">
          <Input
            value={title}
            aria-label="Working title"
            onChange={(event) => onTitleChange(event.target.value)}
            placeholder="Aurora blend launch"
          />
        </Field>

        <Field
          label="Notes, bullets or a link"
          required
          labelAction={
            <span className="pap-tnum text-[11px] text-subtle">
              {words} word{words === 1 ? '' : 's'}
            </span>
          }
        >
          <Textarea
            value={rawContent}
            aria-label="Notes, bullets or a link"
            onChange={(event) => onRawContentChange(event.target.value)}
            onKeyDown={(event) => {
              if (isModifierEnter(event) && ready && !generating) {
                event.preventDefault();
                onGenerate();
              }
            }}
            rows={8}
            maxRows={20}
            placeholder={PLACEHOLDER}
            className="min-h-40 text-[13px]"
          />
        </Field>

        <Field label="Media">
          <MediaStrip
            assets={assets}
            onAdd={onAddMedia}
            onRemove={onRemoveMedia}
            onMove={onMoveMedia}
            onGenerate={onGenerateImage}
            generating={generatingImage}
          />
        </Field>
      </div>

      <div className="space-y-3 border-t border-border px-5 py-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-subtle">Platforms</h3>
          <span className="pap-tnum text-[11px] text-subtle">{selectedPlatforms.length} selected</span>
        </div>
        {connectionNotice}
        <PlatformSelector
          accounts={accounts}
          statuses={platformStatuses}
          selected={selectedPlatforms}
          onToggle={onTogglePlatform}
        />
      </div>

      <div className="border-t border-border px-5 py-4">
        <button
          type="button"
          onClick={() => setShowAdvanced((current) => !current)}
          aria-expanded={showAdvanced}
          className="flex w-full items-center justify-between gap-2 text-xs font-medium uppercase tracking-wide text-subtle transition-colors hover:text-muted"
        >
          Voice & steering
          <ChevronDown
            className={cn('size-3.5 transition-transform', showAdvanced && 'rotate-180')}
            aria-hidden
          />
        </button>

        {showAdvanced && (
          <div className="space-y-4 pt-4">
            <Field
              label="Write in this voice"
              hint={
                styleProfiles.length
                  ? 'Style profiles come from your writing samples.'
                  : 'Add writing samples on the Style page to teach the model your voice.'
              }
            >
              <Select
                value={styleProfileId}
                onChange={(event) => onStyleProfileChange(event.target.value)}
                aria-label="Style profile"
                options={[
                  { value: '', label: 'Automatic — my saved voice' },
                  ...styleProfiles.map((profile) => ({
                    value: profile.id,
                    label: profile.userId ? profile.name : `${profile.name} · workspace default`,
                  })),
                ]}
              />
            </Field>

            <Field
              label="Instructions for this post"
              hint="Optional. Applies to every platform in this run."
            >
              <Textarea
                value={instructions}
                onChange={(event) => onInstructionsChange(event.target.value)}
                rows={2}
                maxRows={6}
                placeholder="Mention that it is members-only for the first week. No emoji."
                className="text-[13px]"
              />
            </Field>
          </div>
        )}
      </div>

      <div className="space-y-3 border-t border-border bg-elevated/30 px-5 py-4">
        {hasExistingDrafts && (
          <div className="flex items-center justify-between gap-3 text-xs text-muted">
            <span>Replace drafts I have already edited</span>
            <Switch
              size="sm"
              checked={overwrite}
              onCheckedChange={onOverwriteChange}
              label="Replace existing drafts"
            />
          </div>
        )}

        <Tooltip
          wrapperClassName="w-full"
          content={
            !rawContent.trim()
              ? 'Add your notes first.'
              : selectedPlatforms.length === 0
                ? 'Pick at least one platform.'
                : 'Generate a draft for every selected platform (⌘↵)'
          }
        >
          <Button
            fullWidth
            size="lg"
            loading={generating}
            disabled={!ready}
            onClick={onGenerate}
            leftIcon={<Wand2 className="size-4" />}
          >
            {hasExistingDrafts ? 'Regenerate drafts' : 'Generate drafts'}
          </Button>
        </Tooltip>

        {aiProvider === 'mock' && (
          <p className="flex items-center gap-1.5 text-[11px] text-subtle">
            <Badge tone="warning" size="sm">
              <Sparkles className="size-2.5" aria-hidden />
              Demo AI
            </Badge>
            No Gemini key configured — drafts are deterministic sample copy.
          </p>
        )}
      </div>
    </Card>
  );
}
